import React, { useState } from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import ContactSignUp from './ContactSignUp';
import ContactSupport from './ContactSupport';

function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    comments: '',
    submitted: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    comments: false
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear error for the field being edited
    if (fieldErrors[e.target.name]) {
      setFieldErrors({
        ...fieldErrors,
        [e.target.name]: false
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Check for empty fields
    const emptyFields = Object.keys(formData).filter(key => 
      key !== 'submitted' && !formData[key].trim()
    );
    
    if (emptyFields.length > 0) {
      const newFieldErrors = {...fieldErrors};
      emptyFields.forEach(field => {
        newFieldErrors[field] = true;
      });
      
      setFieldErrors(newFieldErrors);
      setError('All fields are required');
      setLoading(false);
      return false;
    }

    try {
      // First validate with our API
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      // Get the raw response text
      const responseText = await response.text();
      
      try {
        // Try to parse the response as JSON
        const data = JSON.parse(responseText);
        
        if (response.ok) {
          // Only send email if validation passes
            // Success case
            setFormData({
              firstName: '',
              lastName: '',
              email: '',
              comments: '',
              submitted: true
            });
            
            setLoading(false);
            return true;
        } else {
          // Handle validation errors or other issues
          if (data.field) {
            // If the server specifies which field has an error
            setFieldErrors({
              ...fieldErrors,
              [data.field]: true
            });
          } else if (data.message && data.message.toLowerCase().includes('email')) {
            // If the error message mentions email
            setFieldErrors({
              ...fieldErrors,
              email: true
            });
          }
          
          setError(data.message || 'There was an error sending your message.');
          setLoading(false);
          return false;
        }
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Server response was not valid JSON');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'There was an error sending your message. Please try again.');
      setLoading(false);
      return false;
    }
  };

  return (
    <>
      <section className="theme-light section m-0 p-0" data-theme="light">
        {/* Start flex container */}
        <div className='fullHeight is-flex is-flex-direction-column is-justify-content-space-between'>
        
            {/* Navigation */}
            <div className='columns is-flex is-flex-direction-rows is-justify-content-center'>
              <div className="m-0 p-0 column is-11"><Navigation /></div>
            </div>
            
            {/* Content */}          
            <div className='pb-6 columns is-flex is-flex-wrap-wrap is-flex-direction-rows is-justify-content-center main-content'>
                <div className="m-0 px-6 content column is-4-fullhd is-4-widescreen is-5-desktop is-10-tablet is-12-mobile">
                    <h1 className='pt-4 pb-2 m-0'>Contact us</h1>
                    
                    {!formData.submitted ? (
                        <>
                        <p className=''>Please fill out the form below to contact the Nudgemail team. Mr. Nudges reviews each request while our team sips artisanal coffee.</p>
                        
                        <form 
                          onSubmit={async (e) => {
                            e.preventDefault();
                            const success = await handleSubmit(e);
                            if (success) {
                              setFormData(prev => ({...prev, submitted: true}));
                            }
                          }} 
                          className=""
                          noValidate // Add noValidate to disable browser validation tooltips
                        >
                            <div className="pt-1 field">
                                <label className="label">First name
                                {fieldErrors.firstName && (
                                  <span className="help colorDanger mb-1">First name is required</span>
                                )}
                                </label>
                                <div className="control">
                                    <input
                                    className={`input ${fieldErrors.firstName ? 'colorDanger error' : ''}`}
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    style={{
                                      color: fieldErrors.firstName ? '#ff0000' : 'inherit'
                                    }}
                                    required
                                    />
                                </div>
                            </div>

                            <div className="pt-1 field">
                                <label className="label">Last name
                                {fieldErrors.lastName && (
                                  <span className="help colorDanger mb-1">Last name is required</span>
                                )}
                                </label>
                                <div className="control">
                                    <input
                                    className={`input ${fieldErrors.lastName ? 'colorDanger error' : ''}`}
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    style={{
                                      color: fieldErrors.lastName ? '#ff0000' : 'inherit'
                                    }}
                                    required
                                    />
                                </div>
                            </div>

                            <div className="pt-1 field">
                                <label className="label">Email address  
                                {fieldErrors.email && (
                                  <span className="help colorDanger mb-1">Email address is required</span>
                                )}
                                </label>
                                <div className="control">
                                    <input
                                    className={`input ${fieldErrors.email ? 'colorDanger error' : ''}`}
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    style={{
                                      color: fieldErrors.email ? '#ff0000' : 'inherit'
                                    }}
                                    required
                                    />
                                </div>
                            </div>

                            <div className="pt-1 field">
                                <label className="label">How can we help?
                                {fieldErrors.comments && (
                                  <span className="help colorDanger mb-1">'How can we help?' is required</span>
                                )}
                                </label>
                                <div className="control">
                                    <textarea
                                    className={`textarea ${fieldErrors.comments ? 'colorDanger error' : ''}`}
                                    name="comments"
                                    value={formData.comments}
                                    onChange={handleChange}
                                    style={{
                                      color: fieldErrors.comments ? '#ff0000' : 'inherit'
                                    }}
                                    required
                                    />
                                </div>
                            </div>

                            <div className="pt-5 field">
                                <div className="control">
                                    <button 
                                    className={`button is-primary ${loading ? 'is-loading' : ''}`}
                                    type="submit"
                                    disabled={loading}
                                    >
                                    Submit
                                    </button>
                                </div>
                            </div>
                        </form>
                        </>
                    ) : (
                        <div className="content">
                            <h2 className='p-0 m-0 is-semibold'>You did it! Message sent.</h2>
                            <p className='pt-1 mt-0'>Mr. Nudges, our AI-power backend developer, is now dutifully nudging your message to our small but mighty team of sleep-deprived humans—who built this thing because they love the joy (and deep, personal satisfaction) of being organized. </p>

                            <p>One of the Nudgemail humans will respond within 24 hours,  though, let's be honest, they usually crack under pressure and reply sooner. In the meantime, bask in the fleeting clarity of knowing at least one thing is officially off your plate. </p>

                            <p>Although technically… you just sent a message, so it's not really off your plate. You know, this would be a great time to send a Nudge and remind yourself to follow up—just saying!</p>
                        </div>
                    )}
                </div>
                <div className="p-6 content column is-4-fullhd is-4-widescreen is-5-desktop is-10-tablet is-12-mobile is-flex is-flex-direction-column is-justify-content-center is-align-items-center">
                    <ContactSignUp />
                    <ContactSupport />
                </div>
            </div> 
          
            {/* Footer */}
            <div className='footer m-0 p-0 columns is-flex is-flex-direction-rows is-justify-content-center is-align-items-center'>
                <div className="m-0 p-0 column is-11 is-10-mobile"><Footer /></div>
            </div>
        </div>
        {/* End flex container */}
      </section>
    </>
  );
}

export default Contact;
